import { PracticeShaperApi } from '@agilelab/plugin-wb-practice-shaper-common';
import {
  ApiHolder,
  IdentityApi,
  identityApiRef,
  useApi,
  useApiHolder,
} from '@backstage/core-plugin-api';
import {
  ComponentType,
  DomainType,
  SystemPrototypeType,
  SystemType,
  Taxonomy,
} from '@agilelab/plugin-wb-builder-common';
import { useQuery } from '@tanstack/react-query';
import { practiceShaperApiRef } from '../../../../plugin';
import { hasCatalogProcessingErrors } from '@agilelab/plugin-wb-builder-catalog';

const fetchData = async (
  api: PracticeShaperApi,
  identityApi: IdentityApi,
  apiHolder: ApiHolder,
) => {
  const credentials = await identityApi.getCredentials();

  const fetchError = async (
    item:
      | Taxonomy
      | DomainType
      | ComponentType
      | SystemType
      | SystemPrototypeType,
  ) => {
    const error = await hasCatalogProcessingErrors(item, {
      apis: apiHolder,
    });
    return { ...item, error };
  };

  try {
    const taxonomiesResponse = await api.getTaxonomies({}, credentials);
    const domainsResponse = await api.getDomainTypes({}, credentials);
    const componentsResponse = await api.getComponentTypes({}, credentials);
    const systemsResponse = await api.getSystemTypes({}, credentials);

    let prototypesResponse;
    try {
      const response = await api.getSystemPrototypes({}, credentials);
      prototypesResponse = response;
    } catch (prototypeError) {
      prototypesResponse = { items: [] };
    }

    const [taxonomies, domains, components, systems, systemPrototypes] =
      await Promise.all([
        (async () =>
          Promise.all(taxonomiesResponse.items.map(t => fetchError(t))))(),
        (async () =>
          Promise.all(domainsResponse.items.map(t => fetchError(t))))(),
        (async () =>
          Promise.all(componentsResponse.items.map(t => fetchError(t))))(),
        (async () =>
          Promise.all(systemsResponse.items.map(t => fetchError(t))))(),
        (async () =>
          Promise.all(
            (prototypesResponse?.items || []).map(t => fetchError(t)),
          ))(),
      ]);

    return {
      taxonomies,
      domains,
      components,
      systems,
      systemPrototypes,
    };
  } catch (error) {
    throw error;
  }
};

export const usePracticeShaperGraphData = () => {
  const identityApi = useApi(identityApiRef);
  const api = useApi(practiceShaperApiRef);
  const apiHolder = useApiHolder();

  const data = useQuery({
    queryKey: ['practice-shaper-graph'],
    queryFn: async () => {
      const result = await fetchData(api, identityApi, apiHolder);
      return result;
    },
  });

  return data;
};
